// Vérification asynchrone de l'email du garant (formulaire de candidature et formulaire de bail).
//
// Utilisation : ajouter data-guarantor-email-check-url sur l'input email, et optionnellement :
//   data-tenant-email-source   : sélecteur des champs email du locataire (input → value, select → option data-email)
//   data-guarantor-name-fields : sélecteurs des champs nom/prénom du garant (l'email devient obligatoire s'ils sont remplis)
//   data-feedback              : sélecteur de l'élément qui affiche le message sous le champ
//   data-disable-submit        : "true" pour désactiver le bouton d'envoi du formulaire tant que l'email bloque
//
// Le serveur revalide à la soumission : ce module n'est qu'une aide à la saisie.
// Les messages locaux reprennent les clés guarantor_email_check.* de config/locales/fr.yml.

const LOCAL_MESSAGES = {
  blank: "Veuillez saisir l'email du garant",
  invalid: "L'email du garant n'est pas valide",
  same_as_tenant: "L'email du garant doit être différent de celui du locataire",
  unavailable: "Vérification impossible pour le moment, le serveur vérifiera à l'envoi"
};

const FEEDBACK_COLORS = {
  blank: 'text-red-600',
  invalid: 'text-red-600',
  same_as_tenant: 'text-red-600',
  taken: 'text-red-600',
  existing_guarantor: 'text-blue-600',
  available: 'text-green-600',
  unavailable: 'text-gray-500',
  throttled: 'text-gray-500'
};

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+$/;

const GuarantorEmailCheck = {
  debounceDelay: 400,
  states: new WeakMap(),

  init() {
    document.addEventListener('turbolinks:load', () => this.setupAll());
    document.addEventListener('DOMContentLoaded', () => this.setupAll());
    // Une page restaurée depuis le cache Turbolinks a perdu ses listeners : on retire la garde
    document.addEventListener('turbolinks:before-cache', () => {
      this.inputs().forEach(input => { delete input.dataset.guarantorCheckInit; });
    });
  },

  inputs() {
    return Array.from(document.querySelectorAll('input[data-guarantor-email-check-url]'));
  },

  setupAll() {
    this.inputs().forEach(input => this.setup(input));
  },

  setup(input) {
    if (input.dataset.guarantorCheckInit === 'true') return;
    input.dataset.guarantorCheckInit = 'true';

    this.states.set(input, { timer: null, seq: 0, pending: null, checkedValue: null, ok: true, status: 'idle' });

    input.addEventListener('input', () => this.schedule(input));
    input.addEventListener('blur', () => this.evaluate(input));

    if (input.form) {
      // Délégation : les champs locataire et nom/prénom peuvent être clonés (formulaire de bail)
      const onRelated = (event) => {
        if (this.relatedElements(input).includes(event.target)) this.schedule(input);
      };
      input.form.addEventListener('input', onRelated);
      input.form.addEventListener('change', onRelated);
    }

    // Valeur déjà présente (préremplissage ou re-rendu après une erreur serveur)
    if (input.value.trim() !== '') this.evaluate(input);
  },

  root(input) {
    return input.form || document;
  },

  relatedElements(input) {
    const selectors = [input.dataset.tenantEmailSource, input.dataset.guarantorNameFields].filter(Boolean).join(',');
    if (!selectors) return [];
    return Array.from(this.root(input).querySelectorAll(selectors));
  },

  tenantEmails(input) {
    const selector = input.dataset.tenantEmailSource;
    if (!selector) return [];
    return Array.from(this.root(input).querySelectorAll(selector))
      .map(el => {
        if (el.tagName === 'SELECT') {
          const option = el.selectedOptions && el.selectedOptions[0];
          return option ? (option.dataset.email || '') : '';
        }
        return el.value || '';
      })
      .map(value => value.trim().toLowerCase())
      .filter(Boolean);
  },

  namesFilled(input) {
    const selector = input.dataset.guarantorNameFields;
    if (!selector) return false;
    return Array.from(this.root(input).querySelectorAll(selector)).some(el => el.value.trim() !== '');
  },

  // Un champ masqué ou désactivé n'est ni vérifié ni bloquant
  isActive(input) {
    return !input.disabled && input.offsetParent !== null;
  },

  schedule(input) {
    const state = this.states.get(input);
    clearTimeout(state.timer);
    state.timer = setTimeout(() => this.evaluate(input), this.debounceDelay);
  },

  // Évalue le champ : règles locales dans le même ordre que le serveur, puis appel réseau.
  // Renvoie une Promise<boolean> (true = utilisable ou non bloquant).
  evaluate(input) {
    const state = this.states.get(input);
    clearTimeout(state.timer);
    const value = input.value.trim().toLowerCase();
    state.checkedValue = value;

    if (!this.isActive(input)) {
      return Promise.resolve(this.apply(input, { ok: true, status: 'idle', message: '' }));
    }
    if (value === '') {
      const blocked = this.namesFilled(input);
      return Promise.resolve(this.apply(input, blocked
        ? { ok: false, status: 'blank', message: LOCAL_MESSAGES.blank }
        : { ok: true, status: 'idle', message: '' }));
    }
    if (!EMAIL_PATTERN.test(value)) {
      return Promise.resolve(this.apply(input, { ok: false, status: 'invalid', message: LOCAL_MESSAGES.invalid }));
    }
    if (this.tenantEmails(input).includes(value)) {
      return Promise.resolve(this.apply(input, { ok: false, status: 'same_as_tenant', message: LOCAL_MESSAGES.same_as_tenant }));
    }
    return this.fetchStatus(input, value);
  },

  fetchStatus(input, value) {
    const state = this.states.get(input);
    const seq = ++state.seq;
    const url = new URL(input.dataset.guarantorEmailCheckUrl, window.location.origin);
    url.searchParams.set('email', value);
    this.tenantEmails(input).forEach(email => url.searchParams.append('tenant_emails[]', email));

    const unavailable = { ok: true, status: 'unavailable', message: LOCAL_MESSAGES.unavailable };
    const promise = fetch(url.toString(), { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(response => response.json().then(data => ({ response, data })))
      .then(({ response, data }) => {
        if (seq !== state.seq) return state.ok; // réponse obsolète
        if (response.ok && data && typeof data.ok === 'boolean') {
          return this.apply(input, { ok: data.ok, status: data.status, message: data.message || '' });
        }
        return this.apply(input, unavailable); // 429, 5xx… : non bloquant, le serveur tranchera
      })
      .catch(() => (seq !== state.seq ? state.ok : this.apply(input, unavailable)))
      .finally(() => { if (seq === state.seq) state.pending = null; });

    state.pending = promise;
    return promise;
  },

  apply(input, { ok, status, message }) {
    const state = this.states.get(input);
    state.ok = ok;
    state.status = status;
    input.setCustomValidity(ok ? '' : message);
    this.renderFeedback(input, status, message);
    this.toggleSubmit(input, ok);
    return ok;
  },

  renderFeedback(input, status, message) {
    const feedback = input.dataset.feedback ? document.querySelector(input.dataset.feedback) : null;
    if (!feedback) return;
    feedback.textContent = message;
    feedback.classList.remove('text-red-600', 'text-green-600', 'text-blue-600', 'text-gray-500');
    if (!message) {
      feedback.classList.add('hidden');
      return;
    }
    feedback.classList.remove('hidden');
    feedback.classList.add(FEEDBACK_COLORS[status] || 'text-gray-500');
  },

  toggleSubmit(input, ok) {
    if (input.dataset.disableSubmit !== 'true' || !input.form) return;
    input.form.querySelectorAll('input[type="submit"], button[type="submit"]').forEach(button => {
      button.disabled = !ok;
    });
  },

  // À appeler avant une soumission programmatique : attend une vérification en cours,
  // réévalue si la valeur a changé, et renvoie une Promise<boolean>.
  ensureValid(input) {
    const state = input && this.states.get(input);
    if (!state || !this.isActive(input)) return Promise.resolve(true);

    const value = input.value.trim().toLowerCase();
    if (state.pending && value === state.checkedValue) return state.pending;
    if (value !== state.checkedValue || state.status === 'idle') return this.evaluate(input);
    return Promise.resolve(state.ok);
  },

  // Efface message, validité et état (section garant masquée ou vidée)
  reset(input) {
    const state = input && this.states.get(input);
    if (!state) return;
    clearTimeout(state.timer);
    state.seq += 1;
    state.pending = null;
    state.checkedValue = null;
    this.apply(input, { ok: true, status: 'idle', message: '' });
  }
};

export default GuarantorEmailCheck;
